import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { addLead, getLeads, updateLead } from '../services/telecalling';
import Button from '../components/buttons/Button';
import Pagination from '../components/tables/pagination/Pagination';
import CustomLoading from '../components/customLoading/CustomLoading';
import SearchBar from '../components/searchBar/SearchBar';
import DateRangePicker from '../components/inputFields/DateRangePicker';
import { BiRefresh, BiFilterAlt } from 'react-icons/bi';
import { FiPhone, FiMail, FiHome, FiPackage } from 'react-icons/fi';
import { MdAdd, MdLocationOn } from 'react-icons/md';
import TelecallingViewDetails from './TelecallingViewDetails';
import ShowModal from '../utils/ShowModal';
import { formatDateTime } from '../utils/FormatDateTime';
import Dropdown from '../components/inputFields/DropDown';
import Breadcrumbs from '../components/BreadCrumbs';
import AddLeadForm from './AddLeadForm';
import 'chart.js/auto';
import { getUserId } from '../helpers/local_storage_helpers';
import { statesOfIndia } from '../constants/states';


function TelecallingView() {
    const [searchText, setSearchText] = useState("");
    const [dateRange, setDateRange] = useState({ start: null, end: null });
    const [statusFilter, setStatusFilter] = useState("all");
    const [sourceFilter, setSourceFilter] = useState("all");
    const [categoryFilter, setCategoryFilter] = useState("all");
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [activeTab, setActiveTab] = useState("recent");
    const [modalIsOpen, setIsOpen] = useState(false);
    const [selectedLead, setSelectedLead] = useState(null);
    const [showAddLeadModal, setShowAddLeadModal] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [openindItem, setOpeningItem] = useState(null);
    const [stateFilter, setStateFilter] = useState("");
    const pageSize = 100;

    const statusOptions = [
        { value: "all", label: "All Status" },
        { value: "pending", label: "Pending" },
        { value: "contacted", label: "Contacted" },
        { value: "interested", label: "Interested" },
        { value: "converted", label: "Converted" },
        { value: "not_interested", label: "Not Interested" },
        { value: "failed", label: "Failed" },
    ];

    const sourceOptions = [
        { value: "all", label: "All Sources" },
        { value: "exhibition", label: "Exhibition" },
        { value: "google_ads", label: "Google Ads" },
        { value: "meta_ads", label: "Meta Ads" },
        { value: "website", label: "Website" },
        { value: "referral", label: "Referral" },
        { value: "social_media", label: "Social Media" },
        { value: 'on_ground', label: 'On Ground' },
        { value: "other", label: "Other" },
    ];

    const categoryOptions = [
        { value: "all", label: "All Categories" },
        { value: "product", label: "Product Inquiry" },
        { value: "retailer", label: "Retailer" },
        { value: "fpo", label: "FPO" },
        { value: "satellite", label: "Satellite" },
        { value: "other", label: "Other" },
    ];

    useEffect(() => {
        fetchData();
    }, [statusFilter, sourceFilter, categoryFilter, activeTab]);

    const buildQueryString = () => {
        let query = `page=${currentPage}&page_size=${pageSize}`;
        if (searchText) query += `&search=${searchText}`;
        if (statusFilter !== "all") query += `&status=${statusFilter}`;
        if (sourceFilter !== "all") query += `&lead_source=${sourceFilter}`;
        if (categoryFilter !== "all") query += `&category=${categoryFilter}`;
        if (dateRange.start) query += `&start_date=${dateRange.start}`;
        if (dateRange.end) query += `&end_date=${dateRange.end}`;
        if (activeTab === "pending") query += `&is_followup=true`;
        if (stateFilter) query += `&state=${stateFilter}`;
        return query;
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const query = buildQueryString();
            let res = await getLeads(query);
            if (res.status === 200) {
                setData(res.data);
            } else {
                toast.error("Unable to fetch leads data");
            }
        } catch (error) {
            console.error("Error fetching leads:", error);
            toast.error("Error fetching leads data.");
        } finally {
            setLoading(false);
        }
    };


    const handleSearch = (query) => {
        setSearchText(query);
        // fetchData();
    };

    useEffect(() => {
        const timeout = setTimeout(() => {
            fetchData();
        }, 300);

        return () => clearTimeout(timeout);
    }, [searchText, dateRange, statusFilter, sourceFilter, categoryFilter, activeTab, stateFilter]);


    const handleDateRangeChange = (range) => {
        setDateRange(range);
        fetchData();
    };

    const handleStateFilterChange = (value) => {
        setStateFilter(value);
        fetchData();
    };

    const resetFilters = () => {
        setCurrentPage(1);
        setSearchText("");
        setDateRange({ start: null, end: null });
        setStatusFilter("all");
        setSourceFilter("all");
        setCategoryFilter("all");
        setStateFilter("");
        setActiveTab("recent");
        fetchData();
    };

    const handlePageChange = (page) => {
        setData({});
        setCurrentPage(page);
    };

    const openLeadDetails = (lead) => {
        setSelectedLead(lead);
        setIsOpen(true);
    };

    const closeModal = () => {
        setIsOpen(false);
        setSelectedLead(null);
    };

    const handleStatusChange = async (leadId, newStatus) => {
        try {
            let res = await updateLead(leadId, newStatus);
            if (res.status === 200) {
                toast.success("Lead status updated");
                fetchData();
            } else {
                toast.error("Failed to update lead status");
            }
        } catch (error) {
            console.error("Error updating lead status:", error);
            toast.error("Error updating lead status");
        }
    };

    const handleAddLeadSubmit = async (values) => {
        const transformedValues = {
            ...values,
            products: values.products.map(p => p.value),
        };

        setIsSubmitting(true);
        try {
            const response = openindItem ? await updateLead(openindItem.id, transformedValues) : await addLead({ ...transformedValues, added_by: getUserId() });

            if (response?.status === 201 || response?.status === 200) {
                fetchData();
                toast.success(openindItem ? "Lead updated successfully" : "Lead Added successfully!");
                setOpeningItem(null);
                setShowAddLeadModal(false);
            } else {
                let errors = response.data.error || response.data.detail || response.data || "Opps! Something went wrong, please try again.";
                toast.error(errors);
            }
        } catch (error) {
            let errors =
                error?.response?.data?.error ||
                "Opps! Something went wrong, please try again.";
            toast.error(errors);
        }
        setIsSubmitting(false);
    };

    const getStatusColorClass = (status) => {
        switch (status?.toLowerCase()) {
            case 'pending': return 'bg-yellow-100 text-yellow-700';
            case 'contacted': return 'bg-blue-100 text-blue-700';
            case 'interested': return 'bg-indigo-100 text-indigo-700';
            case 'converted': return 'bg-green-100 text-green-700';
            case 'not_interested':
            case 'failed': return 'bg-red-100 text-red-700';
            default: return 'bg-gray-100 text-gray-600';
        }
    };

    const renderLeadCard = (lead) => {
        const fullAddress = [
            lead.address_line1,
            lead.district,
            lead.state,
            lead.pincode,
        ]
            .filter(Boolean)
            .join(', ');

        return (
            <div
                key={lead.id}
                className={`${lead.follow_up > 0
                    ? "bg-gradient-to-r from-orange-50 to-amber-50 border-orange-200"
                    : "bg-white border-gray-200"
                    } rounded-xl shadow-sm border-2 p-6 mb-4 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1`}
            >
                {/* Header Section */}
                <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                        <div className="flex items-center gap-3 flex-wrap mb-3">
                            <h3 className="text-lg font-bold text-gray-800 hover:text-primary cursor-pointer transition-colors"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    openLeadDetails(lead);
                                }}>
                                {lead.name}
                            </h3>

                            {/* Status Badges */}
                            <div className="flex items-center gap-2 flex-wrap">
                                {lead.category && (
                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                                        {lead.category}
                                    </span>
                                )}
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200 capitalize">
                                    {lead.lead_source?.replace('_', ' ') || 'Pending'}
                                </span>
                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusColorClass(lead.current_status)}`}>
                                    {lead.current_status}
                                </span>
                                {lead.is_app_user && (
                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                                        App User
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Contact Information */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div className="space-y-3">
                                <div className="flex items-center gap-3 text-sm text-gray-700">
                                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                                        <FiPhone size={16} className="text-blue-600" />
                                    </div>
                                    <span className="font-medium">{lead.phone}</span>
                                </div>

                                {lead.email && (
                                    <div className="flex items-center gap-3 text-sm text-gray-700">
                                        <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                                            <FiMail size={16} className="text-green-600" />
                                        </div>
                                        <span className="font-medium">{lead.email}</span>
                                    </div>
                                )}
                            </div>

                            <div className="space-y-3">
                                {lead.shop_name && (
                                    <div className="flex items-center gap-3 text-sm text-gray-700">
                                        <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                                            <FiHome size={16} className="text-purple-600" />
                                        </div>
                                        <span className="font-medium">{lead.shop_name}</span>
                                    </div>
                                )}

                                {fullAddress && (
                                    <div className="flex items-center gap-3 text-sm text-gray-700">
                                        <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                                            <MdLocationOn size={16} className="text-orange-600" />
                                        </div>
                                        <span className="font-medium">{fullAddress}</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Products Information */}
                        {lead.products && lead.products.length > 0 && (
                            <div className="mb-4">
                                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                    <div className="w-5 h-5 bg-indigo-100 rounded-full flex items-center justify-center">
                                        <FiPackage size={12} className="text-indigo-600" />
                                    </div>
                                    Products ({lead.products.length})
                                </h4>
                                <div className="space-y-3">
                                    {lead.products.map((product, index) => (
                                        <div key={product.id || index} className="flex items-start gap-3 p-3 bg-indigo-50 rounded-lg border border-indigo-100">
                                            {product.product_image && (
                                                <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
                                                    <img
                                                        src={product.product_image}
                                                        alt={product.product_name}
                                                        className="w-full h-full object-cover"
                                                        onError={(e) => {
                                                            e.target.style.display = 'none';
                                                        }}
                                                    />
                                                </div>
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-gray-800 leading-relaxed">
                                                    {product.product_name}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Legacy Product Name (for backward compatibility) */}
                        {(!lead.products || lead.products.length === 0) && lead.product_name && (
                            <div className="flex items-center gap-3 mb-4 p-3 bg-gray-50 rounded-lg">
                                <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                                    <FiPackage size={16} className="text-indigo-600" />
                                </div>
                                <span className="text-sm font-medium text-gray-700">{lead.product_name}</span>
                            </div>
                        )}

                        {/* Last Comment */}
                        {lead.last_comment && (
                            <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                                <div className="flex items-start gap-2">
                                    <div className="w-5 h-5 bg-yellow-200 rounded-full flex items-center justify-center mt-0.5">
                                        <span className="text-yellow-600 text-xs">💬</span>
                                    </div>
                                    <div className="flex-1">
                                        <div className="text-xs font-semibold text-yellow-800 mb-1">Last Comment</div>
                                        <p className="text-sm text-gray-700 leading-relaxed">{lead.last_comment}</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Note/Inquiry */}
                        {lead.note && (
                            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                <div className="flex items-start gap-2">
                                    <div className="w-5 h-5 bg-blue-200 rounded-full flex items-center justify-center mt-0.5">
                                        <span className="text-blue-600 text-xs">📝</span>
                                    </div>
                                    <div className="flex-1">
                                        <div className="text-xs font-semibold text-blue-800 mb-1">Note/Inquiry</div>
                                        <p className="text-sm text-gray-700 leading-relaxed">{lead.note}</p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Right Meta Info */}
                    <div className="text-right ml-6 flex flex-col justify-between items-end text-sm text-gray-600 whitespace-nowrap">
                        {lead.added_by_name && (
                            <div className="space-y-2">
                                <div className="p-2 bg-gray-50 rounded-lg">
                                    <div className="font-semibold text-xs text-gray-800">Added By</div>
                                    <div className="text-xs">{lead.added_by_name}</div>
                                </div>
                                <div className="p-2 bg-gray-50 rounded-lg">
                                    <div className="font-semibold text-xs text-gray-800">Assigned To</div>
                                    <div className="text-xs">{lead.assigned_to_name}</div>
                                </div>
                                <div className="text-xs text-gray-500">{formatDateTime(lead.created_at)}</div>
                            </div>
                        )}

                        {/* Special Indicators */}
                        <div className="mt-3 space-y-2">
                            {lead.repeat_count > 0 && (
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 border border-orange-200">
                                    ×{lead.repeat_count} Repeat
                                </span>
                            )}
                            {lead.follow_up > 0 && (
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200">
                                    Call X{lead.follow_up}
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <div className="flex gap-3">
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                openLeadDetails(lead);
                            }}
                            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors duration-200"
                        >
                            <span>📋</span>
                            View Details
                        </button>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setOpeningItem(lead);
                                setShowAddLeadModal(true)
                            }}
                            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-green-600 bg-green-50 hover:bg-green-100 rounded-lg transition-colors duration-200"
                        >
                            <span>✏️</span>
                            Edit Lead
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const renderAnalytics = () => {
        const sourceData = {
            labels: sourceOptions.slice(1).map(opt => opt.label),
            datasets: [{
                data: [12, 19, 8, 5, 3, 2, 1],
                backgroundColor: [
                    '#3B82F6',
                    '#10B981',
                    '#F59E0B',
                    '#6366F1',
                    '#EC4899',
                    '#8B5CF6',
                    '#64748B'
                ],
                borderWidth: 0
            }]
        };

        const statusData = {
            labels: statusOptions.slice(1).map(opt => opt.label),
            datasets: [{
                data: [8, 5, 3, 2, 1, 1],
                backgroundColor: [
                    '#3B82F6',
                    '#10B981',
                    '#F59E0B',
                    '#6366F1',
                    '#EC4899',
                    '#8B5CF6'
                ],
                borderWidth: 0
            }]
        };

        return (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {/* <div className="bg-white p-3 rounded-lg shadow">
                    <div className="flex items-center gap-2 mb-2">
                        <RiPieChartLine className="text-blue-500" size={16} />
                        <h3 className="font-medium text-sm text-gray-700">Leads by Source</h3>
                    </div>
                    <div className="h-48">
                        <Doughnut
                            data={sourceData}
                            options={{
                                maintainAspectRatio: false,
                                plugins: {
                                    legend: {
                                        position: 'right'
                                    }
                                }
                            }}
                        />
                    </div>
                </div> */}

                {/* <div className="bg-white p-3 rounded-lg shadow">
                    <div className="flex items-center gap-2 mb-2">
                        <RiBarChartFill className="text-blue-500" size={16} />
                        <h3 className="font-medium text-sm text-gray-700">Leads by Status</h3>
                    </div>
                    <div className="h-48">
                        <Bar
                            data={statusData}
                            options={{
                                maintainAspectRatio: false,
                                scales: {
                                    y: {
                                        beginAtZero: true
                                    }
                                }
                            }}
                        />
                    </div>
                </div> */}
            </div>
        );
    };

    const renderNoData = () => (
        <div className="flex flex-col items-center justify-center py-16 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border-2 border-dashed border-blue-200">
            <div className="text-primary mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-20 w-20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">No leads found</h3>
            <p className="text-gray-600 mb-6 text-center max-w-md">Try adjusting your filters or add new leads to get started with your telecalling campaign.</p>
            <Button
                variant="primary"
                onClick={() => setShowAddLeadModal(true)}
                icon={<MdAdd size={18} />}
                className="px-6 py-3 text-base font-semibold bg-primary hover:bg-primary-dark text-white rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
            >
                Add New Lead
            </Button>
        </div>
    );

    return (
        <div className="space-y-6">
            {/* Enhanced Header Section */}
            <div className="bg-gradient-to-r from-cyan-50 to-blue-50 border border-cyan-200 rounded-xl shadow-sm p-6">
                <div className="flex items-center justify-between">
                    {/* Title Section */}
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-cyan-500 rounded-xl flex items-center justify-center shadow-md">
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                        </div>
                        <div>
                            <Breadcrumbs items={[
                                { name: 'Dashboard', link: '/dashboard' },
                                { name: 'Telecalling' },
                            ]} />
                            <h1 className="text-2xl font-bold text-gray-800 mt-1">Telecalling Management</h1>
                            <p className="text-sm text-gray-600">Manage and track your leads effectively</p>
                        </div>
                    </div>

                    {/* Action Section */}
                    <div className="flex items-center gap-3">
                        {/* Lead Statistics */}
                        {data.count > 0 && (
                            <div className="hidden lg:flex items-center gap-4 bg-white/70 backdrop-blur-sm rounded-lg px-4 py-2 border border-cyan-100">
                                <div className="text-center">
                                    <p className="text-xs text-gray-500">Total Leads</p>
                                    <p className="text-lg font-semibold text-cyan-600">
                                        {data.count || 0}
                                    </p>
                                </div>
                                <div className="w-px h-8 bg-cyan-200"></div>
                                <div className="text-center">
                                    <p className="text-xs text-gray-500">Today</p>
                                    <p className="text-lg font-semibold text-blue-600">
                                        {data.data?.filter(lead => {
                                            const today = new Date().toDateString();
                                            return new Date(lead.created_at).toDateString() === today;
                                        }).length || 0}
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Add Lead Button */}
                        <Button
                            variant="primary"
                            onClick={() => setShowAddLeadModal(true)}
                            icon={<MdAdd size={18} />}
                            className="bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-3 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 flex items-center gap-2"
                        >
                            Add New Lead
                        </Button>
                    </div>
                </div>
            </div>

            {/* Analytics Section */}
            {data.count > 0 && renderAnalytics()}

            {/* Enhanced Filters Section */}
            <div className="bg-gradient-to-r from-slate-50 to-cyan-50 border border-cyan-200 rounded-xl shadow-sm p-6">
                {/* Filter Header */}
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-8 h-8 bg-cyan-500 rounded-lg flex items-center justify-center">
                        <BiFilterAlt className="w-4 h-4 text-white" />
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-gray-800">Search & Filters</h3>
                        <p className="text-sm text-gray-600">Find and filter leads with advanced criteria</p>
                    </div>
                    {/* Active Filter Count */}
                    {(searchText || statusFilter !== "all" || sourceFilter !== "all" ||
                        categoryFilter !== "all" || dateRange.start || dateRange.end || stateFilter !== "") && (
                            <div className="ml-auto flex items-center gap-2">
                                <div className="px-2 py-1 bg-cyan-100 text-cyan-700 rounded-full text-xs font-medium">
                                    {[
                                        searchText !== "",
                                        statusFilter !== "all",
                                        sourceFilter !== "all",
                                        categoryFilter !== "all",
                                        dateRange.start !== null || dateRange.end !== null,
                                        stateFilter !== ""
                                    ].filter(Boolean).length} active
                                </div>
                                <button
                                    onClick={resetFilters}
                                    className="px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors flex items-center gap-1.5 text-xs font-medium shadow-sm"
                                    title="Reset All Filters"
                                >
                                    <BiRefresh size={14} />
                                    Reset All
                                </button>
                            </div>
                        )}
                </div>

                {/* Primary Search Row */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 mb-4">
                    {/* Search Bar */}
                    <div className="lg:col-span-8">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Search Leads</label>
                        <SearchBar
                            placeholder="🔍 Search by name, phone, or details..."
                            onSearch={handleSearch}
                            value={searchText}
                            className="h-10 w-full"
                        />
                    </div>

                    {/* Date Range */}
                    <div className="lg:col-span-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
                        <DateRangePicker
                            value={dateRange}
                            onChange={handleDateRangeChange}
                            placeholder="📅 Select date range"
                            className="h-10 w-full"
                        />
                    </div>
                </div>

                {/* Secondary Filters Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* State Filter */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                        <Dropdown
                            options={[
                                { label: "🌍 All States", value: "" },
                                ...(statesOfIndia || []).map((state) => ({
                                    label: state,
                                    value: state
                                }))
                            ]}
                            placeholder="Select State"
                            needSearch={true}
                            value={stateFilter}
                            onChange={handleStateFilterChange}
                            className="h-10 w-full"
                        />
                    </div>

                    {/* Status Filter */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                        <Dropdown
                            options={statusOptions.map(option => ({
                                ...option,
                                label: option.label === "All" ? "📋 All Status" : `📌 ${option.label}`
                            }))}
                            value={statusFilter}
                            onChange={setStatusFilter}
                            className="h-10 w-full"
                        />
                    </div>

                    {/* Source Filter */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Lead Source</label>
                        <Dropdown
                            options={sourceOptions.map(option => ({
                                ...option,
                                label: option.label === "All" ? "📡 All Sources" : `📡 ${option.label}`
                            }))}
                            value={sourceFilter}
                            onChange={setSourceFilter}
                            className="h-10 w-full"
                        />
                    </div>

                    {/* Category Filter */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                        <Dropdown
                            options={categoryOptions.map(option => ({
                                ...option,
                                label: option.label === "All" ? "📂 All Categories" : `📂 ${option.label}`
                            }))}
                            value={categoryFilter}
                            onChange={setCategoryFilter}
                            className="h-10 w-full"
                        />
                    </div>
                </div>

                {/* Filter Actions */}
                <div className="flex justify-between items-center mt-6 pt-4 border-t border-cyan-100">
                    <div className="text-sm text-gray-600">
                        {(searchText || statusFilter !== "all" || sourceFilter !== "all" ||
                            categoryFilter !== "all" || dateRange.start || dateRange.end || stateFilter !== "") && (
                                <span className="flex items-center gap-1">
                                    <span className="w-2 h-2 bg-cyan-500 rounded-full"></span>
                                    Active filters applied
                                </span>
                            )}
                    </div>
                    <div className="text-sm text-gray-500">
                        {data.count > 0 && (
                            <span>Showing {data.count} lead{data.count !== 1 ? 's' : ''}</span>
                        )}
                    </div>
                </div>
            </div>

            {/* Leads List */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
                <div className="flex border-b border-gray-200 mb-6">
                    <button
                        className={`px-6 py-3 text-sm font-semibold transition-all duration-200 ${activeTab === "recent"
                            ? "text-primary border-b-2 border-primary bg-blue-50"
                            : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                            }`}
                        onClick={() => setActiveTab("recent")}
                    >
                        Recent Leads
                        <span className="ml-2 px-2 py-1 text-xs bg-primary text-white rounded-full">
                            {data.count || 0}
                        </span>
                    </button>
                    <button
                        className={`px-6 py-3 text-sm font-semibold transition-all duration-200 ${activeTab === "pending"
                            ? "text-primary border-b-2 border-primary bg-blue-50"
                            : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                            }`}
                        onClick={() => setActiveTab("pending")}
                    >
                        Pending Follow-ups
                        <span className="ml-2 px-2 py-1 text-xs bg-orange-500 text-white rounded-full">
                            {data.results?.filter(lead => lead.follow_up > 0).length || 0}
                        </span>
                    </button>
                </div>

                {loading ? (
                    <div className="flex justify-center py-12">
                        <CustomLoading size={80} />
                    </div>
                ) : (
                    <>
                        {data?.results?.length > 0 ? (
                            <div className="space-y-4">
                                {data.results.map(renderLeadCard)}
                            </div>
                        ) : (
                            renderNoData()
                        )}

                        {data.count > 0 && (
                            <div className="mt-8 pt-6 border-t border-gray-200">
                                <Pagination
                                    currentPage={currentPage}
                                    pageSize={pageSize}
                                    totalEntries={data?.count}
                                    onPageChange={handlePageChange}
                                    data={data}
                                    setData={setData}
                                    compact
                                />
                            </div>
                        )}
                    </>
                )}
            </div>

            <ShowModal
                contentLabel="Lead Details"
                isOpen={modalIsOpen}
                modalWidth="70%"
                onRequestClose={closeModal}
                isSidebar
                componentToPass={
                    <TelecallingViewDetails
                        lead={selectedLead}
                        onClose={closeModal}
                        onStatusChange={(newStatus) => {
                            handleStatusChange(selectedLead.id, newStatus);
                            closeModal();
                        }}
                    />
                }
            />

            <ShowModal
                contentLabel="Add New Lead"
                isOpen={showAddLeadModal}
                modalWidth="60%"
                onRequestClose={() => { setShowAddLeadModal(false); setOpeningItem(null); }}
                componentToPass={
                    <AddLeadForm
                        onSubmit={handleAddLeadSubmit}
                        onCancel={() => { setShowAddLeadModal(false); setOpeningItem(null); }}
                        isSubmitting={isSubmitting}
                        initialValues={openindItem || {}}
                    />
                }
            />
        </div>
    );
}

export default TelecallingView;